import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { subscribeSharedFirestore } from "./sharedFirestoreSubscription";
import { ACCOUNT_CLIENT_EDIT_FIELDS } from "../utils/clientPermissions";
import {
  normalizeClient,
  normalizePan,
  toFirestoreClient,
  toFirestoreClientPartial,
} from "../utils/clientValidation";

export function subscribeToClients(onData, onError) {
  const handleRows = (rows) => onData(rows.map((item) => normalizeClient(item)));

  return subscribeSharedFirestore({
    key: "clients:all",
    onData: handleRows,
    onError,
    start: (emit, emitError) => onSnapshot(
      collection(firestore, "clients"),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export function subscribeToTasks(onData, onError) {
  return subscribeSharedFirestore({
    key: "tasks:all",
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(
      collection(firestore, "tasks"),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export function subscribeToStaff(onData, onError) {
  const handleRows = (rows) => {
    const staffMap = {};
    rows.forEach((item) => {
      const role = String(item.role || "").toLowerCase();
      if (role === "staff") {
        staffMap[item.id] = item.name || item.username ||
          (item.email ? item.email.split("@")[0] : item.id);
      }
    });
    onData(staffMap);
  };

  return subscribeSharedFirestore({
    key: "users:all",
    onData: handleRows,
    onError,
    start: (emit, emitError) => onSnapshot(
      collection(firestore, "users"),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export async function createClient(client) {
  return addDoc(collection(firestore, "clients"), {
    ...toFirestoreClient(client),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateClient(clientId, client) {
  return updateDoc(doc(firestore, "clients", clientId), {
    ...toFirestoreClient(client),
    updatedAt: serverTimestamp(),
  });
}

export async function updateClientLimited(clientId, client) {
  const payload = toFirestoreClientPartial(client, ACCOUNT_CLIENT_EDIT_FIELDS);
  return updateDoc(doc(firestore, "clients", clientId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteClient(clientId) {
  return deleteDoc(doc(firestore, "clients", clientId));
}

export function findDuplicatePan(clients, pan, excludedClientId = null) {
  const normalizedPan = normalizePan(pan);
  if (!normalizedPan) return null;

  return (
    clients.find(
      (client) =>
        client.id !== excludedClientId &&
        normalizePan(client.pan) === normalizedPan
    ) || null
  );
}

export function clientHasLinkedTasks(tasks, clientId) {
  return tasks.some((task) => task.clientId === clientId);
}

export async function updateClientNonDestructive(clientId, client, presentFields = []) {
  const payload = toFirestoreClientPartial(client, presentFields);
  if (!Object.keys(payload).length) return;
  return updateDoc(doc(firestore, "clients", clientId), { ...payload, updatedAt: serverTimestamp() });
}
